import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import "./AdministrativeUnitsCreate.css";

/* Shared creatable-select styles */
const selectStyles = {
    control: (b, s) => ({
        ...b,
        borderColor: s.isFocused ? "#006B3C" : "#D2DDD5",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(0,107,60,.18)" : "none",
        "&:hover": { borderColor: "#006B3C" },
        borderRadius: 6, fontSize: 13, minHeight: 38,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: s.isDisabled ? "#F0F4F1" : s.isFocused ? "#fff" : "#F0F4F1",
        opacity: s.isDisabled ? 0.55 : 1,
    }),
    option: (b, s) => ({
        ...b, fontSize: 13,
        background: s.isSelected ? "#006B3C" : s.isFocused ? "rgba(0,107,60,.07)" : "#fff",
        color: s.isSelected ? "#fff" : "#0E1F14",
    }),
    menu: (b) => ({
        ...b, zIndex: 9999, borderRadius: 10,
        border: "1px solid #D2DDD5",
        boxShadow: "0 4px 20px rgba(1,45,26,.12)",
    }),
    placeholder: (b) => ({ ...b, color: "#9BB0A0", fontSize: 13 }),
    singleValue: (b) => ({ ...b, color: "#0E1F14" }),
};

const AdministrativeUnitsCreate = () => {
    const navigate = useNavigate();

    const [scope, setScope] = useState(null);
    const [sector, setSector] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [subOrganization, setSubOrganization] = useState(null);
    const [subSubOrganization, setSubSubOrganization] = useState(null);
    const [subSubSubOrganization, setSubSubSubOrganization] = useState(null);
    const [description, setDescription] = useState("");
    const [remark, setRemark] = useState("");

    const [sectorOptions, setSectorOptions] = useState([]);
    const [organizationOptions, setOrganizationOptions] = useState([]);
    const [subOrganizationOptions, setSubOrganizationOptions] = useState([]);
    const [subSubOrganizationOptions, setSubSubOrganizationOptions] = useState([]);
    const [subSubSubOrganizationOptions, setSubSubSubOrganizationOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");

    const scopeOptions = [
        { value: "state", label: "State" },
        { value: "lga", label: "LGA" },
    ];

    useEffect(() => {
        if (!scope) return;
        setSector(null); setOrganization(null); setSubOrganization(null);
        setSubSubOrganization(null); setSubSubSubOrganization(null);
        setSectorOptions([]); setOrganizationOptions([]); setSubOrganizationOptions([]);
        setSubSubOrganizationOptions([]); setSubSubSubOrganizationOptions([]);
    }, [scope]);

    useEffect(() => {
        if (!scope) return;
        privateApi.get(`/api/administrative-units/sectors?scope=${scope.value}`).then(res => {
            setSectorOptions(res.data.map(s => ({ value: s.sectorCode, label: `${s.description} (${s.sectorCode})` })));
        });
    }, [scope]);

    useEffect(() => {
        if (!sector || !scope) return;
        privateApi.get(`/api/administrative-units/organizations?sector=${sector.value}&scope=${scope.value}`).then(res => {
            setOrganizationOptions(res.data.map(o => ({ value: o.organizationCode, label: `${o.description} (${o.organizationCode})` })));
        });
        setOrganization(null); setSubOrganization(null); setSubSubOrganization(null); setSubSubSubOrganization(null);
    }, [sector, scope]);

    useEffect(() => {
        if (!organization || !sector || !scope) return;
        privateApi.get(`/api/administrative-units/sub-organizations?sector=${sector.value}&organization=${organization.value}&scope=${scope.value}`).then(res => {
            setSubOrganizationOptions(res.data.map(o => ({ value: o.subOrganizationCode, label: `${o.description} (${o.subOrganizationCode})` })));
        });
        setSubOrganization(null); setSubSubOrganization(null); setSubSubSubOrganization(null);
    }, [organization, sector, scope]);

    useEffect(() => {
        if (!subOrganization || !organization || !sector || !scope) return;
        privateApi.get(`/api/administrative-units/sub-sub-organizations?sector=${sector.value}&organization=${organization.value}&subOrganization=${subOrganization.value}&scope=${scope.value}`).then(res => {
            setSubSubOrganizationOptions(res.data.map(o => ({ value: o.subSubOrganizationCode, label: `${o.description} (${o.subSubOrganizationCode})` })));
        });
        setSubSubOrganization(null); setSubSubSubOrganization(null);
    }, [subOrganization, organization, sector, scope]);

    useEffect(() => {
        if (!subSubOrganization || !subOrganization || !organization || !sector || !scope) return;
        privateApi.get(`/api/administrative-units/sub-sub-sub-organizations?sector=${sector.value}&organization=${organization.value}&subOrganization=${subOrganization.value}&subSubOrganization=${subSubOrganization.value}&scope=${scope.value}`).then(res => {
            setSubSubSubOrganizationOptions(res.data.map(o => ({ value: o.subSubSubOrganizationCode, label: `${o.description} (${o.subSubSubOrganizationCode})` })));
        });
        setSubSubSubOrganization(null);
    }, [subSubOrganization, subOrganization, organization, sector, scope]);

    const handleSubmit = e => {
        e.preventDefault();
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirm = async (input) => {
        if (input !== confirmToken) {
            setNotification({ show: true, message: "Incorrect confirmation code.", type: "error" });
            setConfirmOpen(false);
            return;
        }
        setConfirmOpen(false);
        setLoading(true);
        try {
            await privateApi.post("/api/administrative-units", {
                sectorCode: sector?.value, organizationCode: organization?.value,
                subOrganizationCode: subOrganization?.value,
                subSubOrganizationCode: subSubOrganization?.value,
                subSubSubOrganizationCode: subSubSubOrganization?.value,
                description, remark, scope: scope?.value,
            });
            setNotification({ show: true, message: "Administrative unit created successfully.", type: "success" });
            setScope(null); setDescription(""); setRemark("");
        } catch (err) {
            setNotification({ show: true, message: err.response?.data?.message || "Failed to create administrative unit.", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const fields = [
        { label: "Scope", required: true, opts: scopeOptions, val: scope, set: setScope, disabled: false },
        { label: "Sector", required: true, opts: sectorOptions, val: sector, set: setSector, disabled: !scope },
        { label: "Organization", opts: organizationOptions, val: organization, set: setOrganization, disabled: !sector },
        { label: "Sub Organization", opts: subOrganizationOptions, val: subOrganization, set: setSubOrganization, disabled: !organization },
        { label: "Sub Sub Organization", opts: subSubOrganizationOptions, val: subSubOrganization, set: setSubSubOrganization, disabled: !subOrganization },
        { label: "Sub Sub Sub Organization", opts: subSubSubOrganizationOptions, val: subSubSubOrganization, set: setSubSubSubOrganization, disabled: !subSubOrganization },
    ];

    return (
        <div className="auc-page">
            <div className="auc-card">
                {/* Header */}
                <div className="auc-card-header">
                    <h2>Create Administrative Unit</h2>
                    <p>Define a new organisational unit in the hierarchy</p>
                </div>

                <Notification show={notification.show} message={notification.message}
                    type={notification.type} onClose={() => setNotification(p => ({ ...p, show: false }))} />

                {/* Form */}
                <form className="auc-form-body" onSubmit={handleSubmit}>
                    {fields.map(({ label, required, opts, val, set, disabled }) => (
                        <div className="auc-form-group" key={label}>
                            <label className={`auc-label${required ? " required" : ""}`}>{label}</label>
                            <div className="auc-select-wrap">
                                <CreatableSelect
                                    styles={selectStyles}
                                    options={opts}
                                    value={val}
                                    onChange={set}
                                    isDisabled={disabled}
                                    placeholder={`Select ${label}…`}
                                    isClearable
                                />
                            </div>
                        </div>
                    ))}

                    <div className="auc-form-group">
                        <label className="auc-label required">Description</label>
                        <textarea className="auc-textarea" value={description}
                            onChange={e => setDescription(e.target.value)} required
                            placeholder="Enter a description…" />
                    </div>

                    <div className="auc-form-group">
                        <label className="auc-label">Remark</label>
                        <textarea className="auc-textarea" value={remark}
                            onChange={e => setRemark(e.target.value)}
                            placeholder="Optional remark…" />
                    </div>

                    <div className="auc-actions">
                        <button type="submit" className="auc-btn auc-btn-primary" disabled={loading}>
                            {loading ? "Creating…" : "✓ Create Unit"}
                        </button>
                        <button type="button" className="auc-btn auc-btn-back" onClick={() => navigate(-1)}>
                            ← Back
                        </button>
                    </div>
                </form>
            </div>

            <ConfirmDialog show={confirmOpen} token={confirmToken}
                action="You are about to create a new administrative unit"
                onConfirm={handleConfirm} onCancel={() => setConfirmOpen(false)} />
        </div>
    );
};

export default AdministrativeUnitsCreate;
